import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, CircularProgress } from '@mui/material';
import Papa, { ParseResult, ParseError } from 'papaparse';
import { itemService } from '../services/item.service';

interface ItemImportDialogProps {
  open: boolean;
  onClose: () => void;
  onImported?: () => void;
}

const REQUIRED_HEADERS = [
  'itemCode', 'itemName', 'itemType', 'itemGroup', 'unitOfMeasure', 'sellingPrice', 'reorderLevel', 'isActive'
];

const OPTIONAL_HEADERS = ['costPrice', 'itemSubGroup'];

const ItemImportDialog: React.FC<ItemImportDialogProps> = ({ open, onClose, onImported }) => {
  const [file, setFile] = useState<File | null>(null);
  const [parsing, setParsing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<any[]>([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    setPreview([]);
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
    }
  };

  const handleParse = () => {
    if (!file) return;
    setParsing(true);
    setError(null);
    Papa.parse(file as any, {
      header: true,
      skipEmptyLines: true,
      complete: (results: ParseResult<any>) => {
        setParsing(false);
        // Handle parse errors
        if (results.errors && results.errors.length > 0) {
          setError(results.errors.map(e => e.message).join('; '));
          return;
        }
        // Validate headers
        const headers = results.meta.fields || [];
        const missing = REQUIRED_HEADERS.filter(h => !headers.includes(h));
        if (missing.length > 0) {
          setError(`Missing required columns: ${missing.join(', ')}`);
          return;
        }
        // Process the data to handle empty cost prices
        const processedData = (results.data as any[]).map(item => ({
          ...item,
          // Convert empty or zero cost price to undefined so backend can calculate it
          costPrice: (item.costPrice === '' || item.costPrice === null || item.costPrice === undefined || 
                     item.costPrice === '0' || item.costPrice === 0 || parseFloat(String(item.costPrice)) <= 0) 
                     ? undefined : item.costPrice
        }));
        setPreview(processedData);
      }
    });
  };

  const handleImport = async () => {
    if (!preview.length) return;
    setImporting(true);
    setImportResult(null);
    setError(null);
    try {
      // Send to backend in bulk
      if (typeof itemService.importItems !== 'function') throw new Error('Bulk import is not available. Please contact admin.');
      await itemService.importItems(preview);
      setImportResult(`Imported ${preview.length} items successfully.`);
      if (onImported) onImported();
    } catch (err: any) {
      setError(err.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setPreview([]);
    setError(null);
    setParsing(false);
    setImporting(false);
    setImportResult(null);
    onClose();
  };

  // Sample CSV data
  const sampleCsvHeaders = [...REQUIRED_HEADERS, ...OPTIONAL_HEADERS].join(',') + '\n';
  const sampleCsvRow = 'SRV001,Sample Item,SERVICE,OTHERS,PCS,100.00,5,Active,,TABLETS\n';
  const handleDownloadSample = () => {
    const blob = new Blob([
      sampleCsvHeaders + sampleCsvRow
    ], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'item-import-sample.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Import Items from CSV</DialogTitle>
      <DialogContent>
        <Typography variant="body2" mb={2}>
          Please upload a CSV file with the following columns:<br/>
          <b>Required:</b> {REQUIRED_HEADERS.join(', ')}<br/>
          <b>Optional:</b> {OPTIONAL_HEADERS.join(', ')} (will be auto-calculated if empty)
        </Typography>
        <input
          accept=".csv"
          style={{ display: 'none' }}
          id="item-import-file"
          type="file"
          onChange={handleFileChange}
        />
        <label htmlFor="item-import-file">
          <Button variant="outlined" color="primary" component="span">
            Choose CSV File
          </Button>
        </label>
        <Button sx={{ ml: 2 }} variant="text" color="secondary" onClick={handleDownloadSample}>
          Download Sample CSV
        </Button>
        <Button onClick={handleParse} disabled={!file || parsing} sx={{ mt: 2 }}>
          Parse CSV
        </Button>
        {parsing && <CircularProgress size={20} sx={{ ml: 2 }} />}
        {error && <Typography color="error" mt={2}>{error}</Typography>}
        {preview.length > 0 && (
          <Box mt={2}>
            <Typography variant="subtitle2">Preview ({preview.length} rows)</Typography>
            <Box sx={{ maxHeight: 120, overflow: 'auto', border: '1px solid #eee', p: 1, fontSize: 12 }}>
              <pre>{JSON.stringify(preview.slice(0, 5), null, 2)}{preview.length > 5 ? '\n...more' : ''}</pre>
            </Box>
          </Box>
        )}
        {importResult && <Typography color="success.main" mt={2}>{importResult}</Typography>}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={importing}>Cancel</Button>
        <Button onClick={handleImport} disabled={!preview.length || importing} variant="contained" color="primary">
          {importing ? 'Importing...' : 'Import'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ItemImportDialog;
